// src/pages/dashboard/SupportTicketDetail.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, Download, X, User, Headphones, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { supportService, TicketDetailResponse, TicketMessage } from '../../services/api/support.service';
import { useAuth } from '../../context/AuthContext';
import { useWindow } from '../../context/WindowContext';
import { useSupport } from '../../context/SupportContext';

export default function SupportTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { windowWidth } = useWindow();
  const { markMessagesAsRead, refreshUnreadCount } = useSupport();
  const isMobile = windowWidth < 640;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // States
  const [ticket, setTicket] = useState<TicketDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [filePreviews, setFilePreviews] = useState<{ [key: string]: string }>({});
  const [uploadProgress, setUploadProgress] = useState(0);

  // Auto-refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      if (!sending && ticketId) {
        loadTicketDetails(true);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [ticketId, sending]);

  // Load ticket details
  const loadTicketDetails = async (isRefresh = false) => {
    if (!ticketId) return;
    
    try {
      if (!isRefresh) setLoading(true);
      else setRefreshing(true);
      
      const response = await supportService.getTicketDetails(ticketId);
      setTicket(response);
      
      // Mark messages as read
      if (response.messages && response.messages.length > 0) {
        const lastMessage = response.messages[response.messages.length - 1];
        // Mark as read if the last message is not from the current user
        if (lastMessage.userId !== user?.id) {
          markMessagesAsRead(ticketId, lastMessage.id);
        }
      }
      
      // Scroll to bottom on new messages
      if (isRefresh && response.messages && response.messages.length > (ticket?.messages?.length || 0)) {
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
      if (!isRefresh) {
        toast.error('Failed to load ticket details');
        navigate('/dashboard/support');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTicketDetails();
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    processFiles(Array.from(files));
  };

  // Process files (from input or drop)
  const processFiles = (files: File[]) => {
    const validFiles: File[] = [];

    files.forEach(file => {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf',
                           'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} has invalid format`);
        return;
      }

      validFiles.push(file);
    });

    // Max 5 files per message
    if (attachedFiles.length + validFiles.length > 5) {
      toast.error('Maximum 5 files allowed per message');
      return;
    }

    // Generate previews for images
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews(prev => ({
            ...prev,
            [file.name]: e.target?.result as string
          }));
        };
        reader.readAsDataURL(file);
      }
    });

    setAttachedFiles([...attachedFiles, ...validFiles]);
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  // Remove attached file
  const removeFile = (index: number) => {
    const fileToRemove = attachedFiles[index];
    setAttachedFiles(attachedFiles.filter((_, i) => i !== index));

    // Remove preview
    if (filePreviews[fileToRemove.name]) {
      setFilePreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[fileToRemove.name];
        return newPreviews;
      });
    }
  };

  // Download attachment handler
  const handleDownloadAttachment = async (attachmentId: string, fileName: string) => {
    try {
      await supportService.downloadAttachment(attachmentId, fileName);
      toast.success('Downloading file...');
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  // Send message
  const handleSendMessage = async () => {
    // Allow sending with only files, without text
    if (!newMessage.trim() && attachedFiles.length === 0) {
      toast.error('Please enter a message or attach files');
      return;
    }

    if (!ticketId || !ticket) return;

    // Check if ticket is closed
    if (ticket?.ticket?.status === 'CLOSED') {
      toast.error('This ticket is closed and cannot receive new messages');
      return;
    }

    try {
      setSending(true);
      setUploadProgress(0);

      let response;
      if (attachedFiles.length > 0) {
        // Simulate upload progress (since we don't have real progress tracking)
        const interval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 100);

        response = await supportService.addMessageWithAttachments(
          ticketId,
          newMessage,
          attachedFiles
        );

        clearInterval(interval);
        setUploadProgress(100);
      } else {
        response = await supportService.addMessage(ticketId, { message: newMessage });
      }

      if (response.success) {
        toast.success('Message sent successfully');
        setNewMessage('');
        setAttachedFiles([]);
        setFilePreviews({});
        setUploadProgress(0);

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Always reload ticket details to ensure we have the latest messages
        await loadTicketDetails();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="text-yellow-500" size={20} />;
      case 'IN_PROGRESS':
      case 'IN_COMPLIANCE':
      case 'APPROVED':
      case 'SENT_TO_PROVIDER':
        return <AlertCircle className="text-blue-500" size={20} />;
      case 'RESOLVED':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'REJECTED':
        return <X className="text-red-500" size={20} />;
      case 'CLOSED':
        return <CheckCircle className="text-gray-500" size={20} />;
      default:
        return <AlertCircle className="text-gray-500" size={20} />;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-belobaba-gold"></div>
      </div>
    );
  }

  if (!ticket || !ticket.ticket) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
        <h3 className="text-xl font-medium text-gray-700 mb-2">Ticket not found</h3>
        <button
          onClick={() => navigate('/dashboard/support')}
          className="text-belobaba-gold hover:underline"
        >
          Back to Support
        </button>
      </div>
    );
  }

  const canSendMessage = ticket?.ticket?.status !== 'CLOSED' && ticket?.ticket?.status !== 'RESOLVED';

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-t-xl p-4 sm:p-6 border-b">
        <div className="flex items-start justify-between mb-4">
          <button
            onClick={() => navigate('/dashboard/support')}
            className="flex items-center gap-2 text-gray-600 hover:text-belobaba-gold transition-colors"
          >
            <ArrowLeft size={20} />
            Back to tickets
          </button>
          
          {refreshing && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-belobaba-gold"></div>
              Refreshing...
            </span>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">#{ticket.ticket.ticketNumber}</span>
            <div className="flex items-center gap-2">
              {getStatusIcon(ticket.ticket.status)}
              <span className="font-medium">{ticket.ticket.status.replace('_', ' ')}</span>
            </div>
            <span 
              className="px-2 py-1 rounded-full text-xs font-medium"
              style={{ 
                backgroundColor: `${supportService.getPriorityColor(ticket.ticket.priority)}20`,
                color: supportService.getPriorityColor(ticket.ticket.priority),
                border: `1px solid ${supportService.getPriorityColor(ticket.ticket.priority)}40`
              }}
            >
              {ticket.ticket.priority} Priority
            </span>
          </div>
          
          <h1 className="text-2xl font-medium">{ticket.ticket.subject}</h1>
          
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span>Created: {formatDate(ticket.ticket.createdAt)}</span>
            {ticket.ticket.resolvedAt && (
              <span>Resolved: {formatDate(ticket.ticket.resolvedAt)}</span>
            )}
            {ticket.ticket.closedAt && (
              <span>Closed: {formatDate(ticket.ticket.closedAt)}</span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 bg-gray-50 overflow-y-auto p-4 sm:p-6">
        <div className="space-y-4">
          {/* Initial message */}
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-belobaba-gold flex items-center justify-center text-white font-medium">
                <User size={20} />
              </div>
            </div>
            <div className="flex-1">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">You</span>
                  <span className="text-xs text-gray-500">{formatDate(ticket.ticket.createdAt)}</span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{ticket.ticket.content}</p>
                
                {/* Initial attachments */}
                {ticket.attachments && ticket.attachments.filter(a => !a.messageId).length > 0 && (
                  <div className="mt-3 space-y-2">
                    {ticket.attachments.filter(a => !a.messageId).map((attachment) => (
                      <button
                        key={attachment.id}
                        onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                        className="flex items-center gap-2 text-sm text-belobaba-gold hover:underline"
                      >
                        <Paperclip size={14} />
                        {attachment.fileName}
                        <span className="text-gray-500">({supportService.formatFileSize(attachment.fileSize)})</span>
                        <Download size={14} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          {ticket.messages?.map((message) => {
            // Simple logic: if role is CLIENT, it's the user. Otherwise, it's support.
            const isUserMessage = message.user?.role === 'CLIENT';
            
            return (
              <div key={message.id} className={`flex gap-3`}>
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                    isUserMessage ? 'bg-belobaba-gold' : 'bg-gray-600'
                  }`}>
                    {isUserMessage ? <User size={20} /> : <Headphones size={20} />}
                  </div>
                </div>
                <div className="flex-1">
                  <div className={`rounded-xl p-4 shadow-sm ${
                    isUserMessage ? 'bg-white' : 'bg-gray-100'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">
                        {isUserMessage ? 'You' : 'Support Team'}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(message.createdAt)}</span>
                      {message.isInternal && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Internal</span>
                      )}
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                    
                    {/* Message attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {message.attachments.map((attachment) => (
                          <button
                            key={attachment.id}
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                            className="flex items-center gap-2 text-sm text-belobaba-gold hover:underline"
                          >
                            <Paperclip size={14} />
                            {attachment.fileName}
                            <span className="text-gray-500">({supportService.formatFileSize(attachment.fileSize)})</span>
                            <Download size={14} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      {canSendMessage ? (
        <div className="bg-white border-t p-4 sm:p-6">
          {/* Upload progress bar */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mb-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-belobaba-gold h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Uploading files... {uploadProgress}%</p>
            </div>
          )}

          {/* Drag & Drop Zone for Files */}
          {attachedFiles.length === 0 && (
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mb-3 border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                isDragging
                  ? 'border-belobaba-gold bg-yellow-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload-message"
              />
              <label
                htmlFor="file-upload-message"
                className="cursor-pointer flex items-center justify-center gap-2 text-sm text-gray-600"
              >
                <Paperclip size={18} />
                {isDragging ? 'Drop files here' : 'Click or drag files to attach (max 5, 5MB each)'}
              </label>
            </div>
          )}

          {/* Attached files with previews */}
          {attachedFiles.length > 0 && (
            <div className="mb-3 space-y-2">
              {attachedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-3 bg-white border border-gray-200 px-3 py-2 rounded-lg">
                  {/* Preview thumbnail for images */}
                  {filePreviews[file.name] ? (
                    <img
                      src={filePreviews[file.name]}
                      alt={file.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <Paperclip size={20} className="text-gray-400" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {supportService.formatFileSize(file.size)}
                    </p>
                  </div>

                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                    title="Remove file"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-belobaba-gold"
              disabled={sending}
            />

            <button
              onClick={handleSendMessage}
              disabled={sending || (attachedFiles.length === 0 && !newMessage.trim())}
              className="flex-shrink-0 px-4 py-2 bg-belobaba-gold text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Send size={20} />
              )}
              {isMobile ? '' : 'Send'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border-t border-yellow-200 p-4 text-center">
          <p className="text-yellow-700">
            {ticket?.ticket?.status === 'CLOSED' 
              ? 'This ticket has been closed and cannot receive new messages.'
              : 'This ticket has been resolved. If you need further assistance, please create a new ticket.'}
          </p>
        </div>
      )}
    </div>
  );
}